import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, RotateCcw, Search, Trash2 } from 'lucide-react';
import { DataTable, type Column } from '@/components/ui/DataTable.js';
import { Button } from '@/components/ui/Button.js';
import { Input, Select } from '@/components/ui/Field.js';
import { ConfirmDialog } from '@/components/ui/Modal.js';
import { PublishBadge } from '@/components/ui/Badge.js';
import { useToast } from '@/components/ui/Toast.js';
import { queryKeys } from '@/lib/query-keys.js';
import { dateTime } from '@/lib/format.js';
import { errorMessage } from '@/lib/api-error.js';
import { SortableList } from '@/components/ui/SortableList.js';
import type { ContentApi, ContentListParams, ContentRecord } from './content-api.js';

export type ContentListPageProps<T extends ContentRecord> = {
  title: string;
  /** `queryKeys.content` uchun kolleksiya nomi: `news`, `staff`. */
  collection: string;
  contentApi: ContentApi<T>;
  /** Tahrirlash sahifasining manzili: `/news`. */
  basePath: string;
  createLabel?: string;
  /** Nashr holati va sanadan tashqari ustunlar. */
  columns: ReadonlyArray<Column<T>>;
  emptyTitle?: string;
  emptyHint?: ReactNode;
  /**
   * Tartib surish orqali o'zgartiriladimi (rahbariyat, hujjatlar).
   *
   * Yoqilganda sahifalash o'chadi: 3-sahifadagi elementni 1-sahifaga
   * surib bo'lmaydi va bu foydalanuvchini chalg'itardi.
   */
  sortable?: boolean;
  /** Qo'shimcha filtr paneli (kategoriya tanlash). */
  filters?: ReactNode;
  extraParams?: ContentListParams;
  /**
   * Sarlavha yonidagi qo'shimcha tugmalar.
   *
   * Kategoriyalar va dron-shou bo'limlari yon menyuda alohida band
   * OLMAYDI (D0 — 18 band), shuning uchun ular shu yerdan ochiladi.
   */
  actions?: ReactNode;
};

/**
 * Kontent ro'yxati — 9 ta bo'lim uchun bitta ekran.
 *
 * Qidiruv, nashr filtri, savatcha, sahifalash va tartib surish shu yerda.
 * Har bir bo'lim faqat O'Z ustunlarini beradi.
 */
export function ContentListPage<T extends ContentRecord>({
  title,
  collection,
  contentApi,
  basePath,
  createLabel = 'Qo‘shish',
  columns,
  emptyTitle,
  emptyHint,
  sortable = false,
  filters,
  extraParams,
  actions,
}: ContentListPageProps<T>) {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [published, setPublished] = useState('');
  const [trash, setTrash] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<T | null>(null);

  const params = useMemo<ContentListParams>(
    () => ({
      page: sortable ? 1 : page,
      limit: sortable ? 100 : 20,
      search: search.trim() || undefined,
      isPublished: published === '' ? undefined : published === 'true',
      deleted: trash || undefined,
      sort: 'order',
      ...extraParams,
    }),
    [page, search, published, trash, sortable, extraParams],
  );

  const list = useQuery({
    queryKey: queryKeys.content.list(collection, params),
    queryFn: ({ signal }) => contentApi.list(params, signal),
  });

  const invalidate = useCallback(
    () => queryClient.invalidateQueries({ queryKey: queryKeys.content.all(collection) }),
    [queryClient, collection],
  );

  const remove = useMutation({
    mutationFn: (id: string) => contentApi.remove(id),
    onSuccess: async () => {
      toast.success('Savatchaga o‘tkazildi');
      setPendingDelete(null);
      await invalidate();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const restore = useMutation({
    mutationFn: (id: string) => contentApi.restore(id),
    onSuccess: async () => {
      toast.success('Tiklandi');
      await invalidate();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const reorder = useMutation({
    mutationFn: (items: ReadonlyArray<{ _id: string; order: number }>) => contentApi.reorder(items),
    onSuccess: async () => {
      toast.success('Tartib saqlandi');
      await invalidate();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const rows = list.data?.data ?? [];

  const allColumns = useMemo<ReadonlyArray<Column<T>>>(
    () => [
      ...columns,
      {
        key: 'isPublished',
        header: 'Holat',
        width: '140px',
        render: (row) => <PublishBadge isPublished={row.isPublished} />,
      },
      {
        key: 'updatedAt',
        header: 'O‘zgartirilgan',
        width: '170px',
        hideOnTablet: true,
        render: (row) => <span className="text-n-600">{dateTime(row.updatedAt)}</span>,
      },
      {
        key: 'actions',
        header: '',
        align: 'right',
        width: '60px',
        render: (row) =>
          trash ? (
            <Button
              size="sm"
              variant="ghost"
              aria-label="Tiklash"
              onClick={(event) => {
                event.stopPropagation();
                restore.mutate(row._id);
              }}
            >
              <RotateCcw className="size-4" aria-hidden />
            </Button>
          ) : (
            <Button
              size="sm"
              variant="ghost"
              aria-label="O‘chirish"
              onClick={(event) => {
                event.stopPropagation();
                setPendingDelete(row);
              }}
            >
              <Trash2 className="size-4 text-error-600" aria-hidden />
            </Button>
          ),
      },
    ],
    [columns, trash, restore],
  );

  return (
    <div className="space-y-4 p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold text-n-900">{title}</h1>
        <div className="flex flex-wrap items-center gap-2">
          {actions}
          {!trash && (
            <Button icon={<Plus className="size-4" />} onClick={() => navigate(`${basePath}/new`)}>
              {createLabel}
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-56 flex-1">
          <Input
            placeholder="Qidirish…"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
            suffix={<Search className="size-4 text-n-400" aria-hidden />}
          />
        </div>

        {filters}

        <Select
          aria-label="Nashr holati"
          value={published}
          placeholder="Barcha holatlar"
          options={[
            { value: 'true', label: 'Nashr qilingan' },
            { value: 'false', label: 'Qoralama' },
          ]}
          onChange={(event) => {
            setPublished(event.target.value);
            setPage(1);
          }}
          className="w-48"
        />

        <Button
          variant={trash ? 'primary' : 'outline'}
          icon={<Trash2 className="size-4" />}
          onClick={() => {
            setTrash((current) => !current);
            setPage(1);
          }}
        >
          Savatcha
        </Button>
      </div>

      {sortable && !trash ? (
        <SortableList
          items={rows}
          getId={(row) => row._id}
          onReorder={(ordered) =>
            reorder.mutate(ordered.map((row, index) => ({ _id: row._id, order: index })))
          }
          loading={list.isLoading}
          emptyTitle={emptyTitle ?? 'Hozircha yozuv yo‘q'}
          renderRow={(row) => (
            <div className="flex flex-1 items-center gap-3">
              <button
                type="button"
                onClick={() => navigate(`${basePath}/${row._id}`)}
                className="flex-1 truncate text-left text-sm font-medium text-n-800 hover:text-brand-700"
              >
                {columns[0]?.render(row)}
              </button>
              <PublishBadge isPublished={row.isPublished} />
              <Button
                size="sm"
                variant="ghost"
                aria-label="O‘chirish"
                onClick={() => setPendingDelete(row)}
              >
                <Trash2 className="size-4 text-error-600" aria-hidden />
              </Button>
            </div>
          )}
        />
      ) : (
        <DataTable
          columns={allColumns}
          rows={rows}
          rowKey={(row) => row._id}
          loading={list.isLoading}
          onRowClick={trash ? undefined : (row) => navigate(`${basePath}/${row._id}`)}
          emptyTitle={trash ? 'Savatcha bo‘sh' : (emptyTitle ?? 'Hozircha yozuv yo‘q')}
          emptyHint={trash ? undefined : emptyHint}
          meta={list.data?.meta}
          onPageChange={setPage}
        />
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        title="Savatchaga o‘tkazish"
        message="Yozuv saytdan olib tashlanadi va savatchaga tushadi. Uni keyin tiklash mumkin."
        confirmLabel="O‘tkazish"
        danger
        loading={remove.isPending}
        onConfirm={() => pendingDelete && remove.mutate(pendingDelete._id)}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
}
